import React, { useRef, useState } from 'react'
import Reveal from './Reveal'
import SectionHead from './SectionHead'
import Credit from './Credit'
import { useLightbox } from './Lightbox'
import { useAreas, usePhotos, usePlaces, useDistance } from '../i18n/hooks'
import { useI18n } from '../i18n/LanguageContext'

function AreaGallery({ photos }) {
  const { t } = useI18n()
  const [index, setIndex] = useState(0)
  const { open } = useLightbox()
  const current = photos[index]

  return (
    <div className="area-gallery">
      <figure className="area-figure">
        <button
          type="button"
          className="area-figure-button"
          onClick={() => open(photos, index)}
          aria-label={`${t.common.enlargePrefix} ${current.caption}`}
        >
          <img
            key={current.id}
            src={current.src}
            alt={current.alt}
            width={current.width}
            height={current.height}
            loading="lazy"
          />
          <span className="zoom-hint" aria-hidden="true">{t.common.zoomHint}</span>
        </button>
        <figcaption>
          <span>{current.caption}</span>
          <Credit photo={current} />
        </figcaption>
      </figure>
      {photos.length > 1 && (
        <ul className="thumbs">
          {photos.map((photo, i) => (
            <li key={photo.id}>
              <button
                type="button"
                className={i === index ? 'is-current' : undefined}
                aria-label={`${t.common.showPhotoPrefix} ${photo.caption}`}
                aria-current={i === index ? 'true' : undefined}
                onClick={() => setIndex(i)}
              >
                <img src={photo.src} alt="" loading="lazy" width={photo.width} height={photo.height} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Explore({ active, onChange }) {
  const { t } = useI18n()
  const areas = useAreas()
  const { photosFor } = usePhotos()
  const { getPlace } = usePlaces()
  const distance = useDistance()
  const tabRefs = useRef({})

  const onKeyDown = (event, index) => {
    const keys = { ArrowRight: 1, ArrowLeft: -1 }
    let next
    if (event.key in keys) next = (index + keys[event.key] + areas.length) % areas.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = areas.length - 1
    if (next === undefined) return
    event.preventDefault()
    onChange(areas[next].id)
    tabRefs.current[areas[next].id]?.focus()
  }

  return (
    <section className="section section-alt" id="explore" aria-labelledby="explore-title">
      <div className="container">
        <SectionHead id="explore-title" kicker={t.explore.kicker} title={t.explore.title} lead={t.explore.lead} />
        <Reveal>
          <div className="tabs" role="tablist" aria-label={t.common.areasTablistAria}>
            {areas.map((area, index) => (
              <button
                key={area.id}
                ref={(el) => { tabRefs.current[area.id] = el }}
                type="button"
                role="tab"
                id={`tab-${area.id}`}
                aria-selected={active === area.id}
                aria-controls={`panel-${area.id}`}
                tabIndex={active === area.id ? 0 : -1}
                className={`tab${active === area.id ? ' is-active' : ''}`}
                onClick={() => onChange(area.id)}
                onKeyDown={(event) => onKeyDown(event, index)}
              >
                {area.label}
              </button>
            ))}
          </div>
        </Reveal>
        {areas.map((area) => (
          <div
            key={area.id}
            role="tabpanel"
            id={`panel-${area.id}`}
            aria-labelledby={`tab-${area.id}`}
            hidden={active !== area.id}
            className="tab-panel"
          >
            <AreaGallery photos={photosFor(area.photos)} />
            <div className="area-text">
              <p className="kicker kicker-small">{area.kicker}</p>
              <h3>{area.title}</h3>
              <p className="area-lead">{area.lead}</p>
              {area.paragraphs.map((text) => (
                <p key={text}>{text}</p>
              ))}
              {area.note && <p className="note">{area.note}</p>}
              <dl className="fact-grid">
                {area.facts.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value ?? distance(getPlace(area.id))}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export default Explore
